import { Link } from 'react-router-dom';
import { Shield, Target, Users, Scale, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_protect-florida-now/artifacts/rcbvjapp_D35BB94A-6F16-417E-8EBA-069AD1D48703.PNG";
const PROGRAMS_IMG = "https://customer-assets.emergentagent.com/job_protect-florida-now/artifacts/ppi2v49t_9D14A9FD-5222-4C7E-8965-AADBD6B54C57.PNG";

const values = [
  { icon: Shield, title: 'Protection', desc: 'Safeguarding Florida families from threats to their communities and way of life.' },
  { icon: Target, title: 'Preservation', desc: 'Strategically preserving land, wildlife habitats, and natural waters.' },
  { icon: Users, title: 'Unity', desc: 'Bringing diverse communities together with one voice and one mission.' },
  { icon: Scale, title: 'Justice', desc: 'Funding legal defense to protect community rights and interests.' },
  { icon: Heart, title: 'Service', desc: 'Dedicated to serving the people of Florida through direct action.' },
];

export default function About() {
  return (
    <div className="pt-16" data-testid="about-page">
      {/* Header */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">About Us</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">Our Story & Mission</h1>
          <p className="text-lg text-white/80 font-body max-w-2xl">A 501(c)(4) social welfare organization dedicated to building stronger Florida communities through collective action.</p>
        </div>
      </section>

      {/* About Content with branded image */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy mb-6">Who We Are</h2>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                Safe Florida is a 501(c)(4) social welfare organization headquartered in Port St. Lucie, Florida. Founded with a clear mission: to organize communities, fund legal action, preserve strategic land, and build membership-backed statewide influence.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                We believe that every Florida family deserves a voice in the decisions that shape their communities. Through our membership-driven model, we pool resources and expertise to take meaningful action where it matters most.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed">
                From the Treasure Coast to communities across the state, Safe Florida stands as a unified force for protection, preservation, and progress.
              </p>
            </div>
            <img src={ABOUT_IMG} alt="Safe Florida - Veterans, families, and community" className="rounded-lg w-full object-cover" style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }} />
          </div>
        </div>
      </section>

      {/* F.C. Clark & Board Recruitment */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="fc-clark-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <img src={PROGRAMS_IMG} alt="Safe Florida Board Member Recruitment" className="rounded-lg w-full object-cover" style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }} />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Leadership</p>
              <h2 className="text-3xl font-heading font-bold text-navy mb-2">F.C. Clark</h2>
              <p className="font-signature text-gold text-3xl mb-6">F.C. Clark</p>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                With deep roots in Florida's communities, F.C. Clark has dedicated his career to civic engagement, community organizing, and protecting the interests of Florida families.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                As the founder and leader of Safe Florida, F.C. Clark brings decades of experience in community advocacy, land preservation, and building coalitions that make real change happen.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                His vision: Leadership. Accountability. Public Safety. Supporting law enforcement, strengthening community safety, protecting Florida families, and promoting responsible growth.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed">
                His vision for Safe Florida is simple: build a movement where every member has a seat at the table, every family is protected, and every community is strengthened.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Our Values</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy">What Drives Us</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="text-center p-6 border border-gray-200 rounded-lg card-hover">
                <div className="w-12 h-12 mx-auto mb-4 bg-navy/5 rounded-lg flex items-center justify-center">
                  <Icon className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-heading font-bold text-navy mb-2">{title}</h3>
                <p className="text-sm text-gray-600 font-body">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Join Our Movement</h2>
          <p className="text-lg text-white/80 font-body mb-8">Stand with F.C. Clark and thousands of Floridians building a stronger future.</p>
          <Link to="/programs/membership" data-testid="about-cta-join">
            <Button size="lg" className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-10 rounded-md">BECOME A MEMBER</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
